'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { useViewportSize } from '@mantine/hooks'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LinePlus } from '@qr-front/icons/line-plus'
import { Button } from '@qr-front/ui/components/Button'
import { useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useBus } from 'react-bus'
import { FormProvider, useForm } from 'react-hook-form'

import { transformBackendResponseKey } from '@/config'
import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import {
	maxLength,
	scrollToErrorWithAccordion,
	validateContentForm,
} from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { RhcField } from '../../components/RhcField'
import { OPEN_CONTENT_ACCORDIONS_EVENT } from '../../contentFormConstants'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import {
	BUSINESS_INFO_COMPANY_NAME_MAX_LENGTH,
	BUSINESS_INFO_SUBTITLE_MAX_LENGTH,
	BUSINESS_INFO_TITLE_MAX_LENGTH,
	getBusinessInfoFormSchema,
	TBusinessInfoFields,
} from './businessInfoFormModel'
import { businessInfoButtonListAtom, ButtonsSection } from './ButtonsSection'

export const BUSINESS_INFO_FORM_ID = 'business-info'

export const businessInfoCompanyNameAtom = atomWithReset('')
export const businessInfoTitleAtom = atomWithReset<string | undefined>('')
export const businessInfoSubtitleAtom = atomWithReset<string | undefined>('')

export const resetBusinessInfoForm = () => {
	getStore().set(businessInfoCompanyNameAtom, RESET)
	getStore().set(businessInfoTitleAtom, RESET)
	getStore().set(businessInfoSubtitleAtom, RESET)
	getStore().set(businessInfoButtonListAtom, RESET)
}

export const BusinessInfoForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const { width } = useViewportSize()
	const formRef = useRef<HTMLFieldSetElement>(null)
	const bus = useBus()

	const form = useForm<TBusinessInfoFields>({
		defaultValues: {
			companyName: getStore().get(businessInfoCompanyNameAtom),
			title: getStore().get(businessInfoTitleAtom),
			subtitle: getStore().get(businessInfoSubtitleAtom),
			buttons: getStore().get(businessInfoButtonListAtom),
		},
		mode: 'onTouched',
		resolver: yupResolver(getBusinessInfoFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid, errors },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: BUSINESS_INFO_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				let mappedProperty = property
				if (property === 'businessInfo.companyName') {
					mappedProperty = 'companyName'
				} else if (property === 'businessInfo.title') {
					mappedProperty = 'title'
				} else if (property === 'businessInfo.subtitle') {
					mappedProperty = 'subtitle'
				} else if (property.startsWith('businessInfo.buttons.')) {
					mappedProperty = property.replace(
						'businessInfo.buttons.',
						'buttons.',
					)
				}

				form.setError(
					mappedProperty as Parameters<typeof form.setError>[0],
					{
						message: translatedMessage,
					},
				)

				scrollToErrorWithAccordion({
					form,
					bus,
					formId: BUSINESS_INFO_FORM_ID,
					formRef,
					animationDuration: GENERATOR_FORM_ANIMATION_DURATION * 1.25,
					openAccordionEvent: OPEN_CONTENT_ACCORDIONS_EVENT,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) => handleSubmit(onValid, onInvalid),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit, errors],
	)

	const { getValues, setFocus, setValue } = form

	useSyncFieldWithAtom({
		atom: businessInfoButtonListAtom,
		setFieldValue: (value) => setValue('buttons', value),
	})

	const companyNameField = (
		<RhcField
			type="text"
			required
			form={form}
			path="companyName"
			fieldName="company_name"
			atom={businessInfoCompanyNameAtom}
			onChangeTransform={(event) =>
				maxLength(BUSINESS_INFO_COMPANY_NAME_MAX_LENGTH + 1)(event)
			}
			label={t(
				'generator__content_form_section__about__company_section__name',
			)}
			placeholder={t(
				'generator__content_form_section__business_info__input__company_name_description',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const titleField = (
		<RhcField
			type="text"
			form={form}
			path="title"
			fieldName="title"
			atom={businessInfoTitleAtom}
			onChangeTransform={(event) =>
				maxLength(BUSINESS_INFO_TITLE_MAX_LENGTH + 1)(event)
			}
			label={t(
				'generator__content_form_section__business_info__input__title_label',
			)}
			placeholder={t(
				'generator__content_form_section__business_info__input__title_placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const subtitlePlaceholder =
		width < 740
			? t(
					'generator__content_form_section__business_info__input__subtitle_placeholder_mobile',
				)
			: t(
					'generator__content_form_section__business_info__input__subtitle_placeholder',
				)

	const subtitleField = (
		<RhcField
			type="text"
			form={form}
			path="subtitle"
			fieldName="subtitle"
			atom={businessInfoSubtitleAtom}
			onChangeTransform={(event) =>
				maxLength(BUSINESS_INFO_SUBTITLE_MAX_LENGTH + 1)(event)
			}
			label={t(
				'generator__content_form_section__business_info__input__subtitle_label',
			)}
			placeholder={subtitlePlaceholder}
			className="col-span-2"
		/>
	)

	const setButtonList = useSetAtom(businessInfoButtonListAtom)

	const handleClickAddButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		setButtonList((prev) => [...prev, { title: '', url: '' }])
		setTimeout(
			() => setFocus(`buttons.${getValues('buttons')!.length - 1}.title`),
			0,
		)
	}

	return (
		<fieldset ref={formRef}>
			<div className="grid grid-cols-2 gap-x-12 gap-y-4">
				{companyNameField}
				{titleField}
				{subtitleField}
			</div>

			<FormProvider {...form}>
				<ButtonsSection />
			</FormProvider>

			<Button
				visual="soft"
				// Using pointer-down instead of click to avoid button shifting when active input loses focus and validates
				onPointerDown={handleClickAddButton}
				className="mt-6 tablet:mt-8 [&>svg]:size-4"
			>
				<LinePlus />
				{t(
					'generator__content_form_section__business_info__add_button',
				)}
			</Button>
		</fieldset>
	)
})

BusinessInfoForm.displayName = 'BusinessInfoForm'

export const businessInfoFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: BUSINESS_INFO_FORM_ID,
	title: t('generator__content_form_section__business_info__title'),
	description: t(
		'generator__content_form_section__business_info__description',
	),
	icon: <LineInfoCircle />,
	Content: BusinessInfoForm,
})
